import { Controller } from "@hotwired/stimulus"

// Sentinel option value for "create the suggested grade" — the matching
// hidden rows[N][new_grade_name] input carries the actual name; the server
// ignores grade_id whenever new_grade_name is present.
const NEW_GRADE_VALUE = "__new__"

// Light stemming + stopwords mirroring PriceListReviewBuilder, so the
// client-side group auto-fill agrees with the server's grade resolution.
const STOPWORDS = new Set(["colors", "color", "and", "the", "premium"])
const STEMS = { specialty: "special", neons: "neon" }

// Review screen for an uploaded price sheet (price_lists/review_upload).
// One instance wraps the whole form. Confident rows render collapsed to a
// plain sentence with their pickers hidden-but-present (so the form still
// posts the resolved match); rows that need a human render open.
//
//  - editMatch: reveals a settled row's hidden pickers ("Change").
//  - familyChanged: repopulates the price-group + variant pickers for the new
//    product, auto-fills the price group from the sheet's wording, flips
//    rows[N][learn] so the server learns the operator's mapping, and offers
//    to apply the same product to sibling rows (the 13"/20"/40" of one item).
//  - applyCascade: applies this row's product to the other unmatched rows
//    that share its base name.
//  - gradeChanged / variantChanged / variantToggled / listChanged: unchanged
//    grade-vs-override + target-list plumbing.
//  - skipToggled / recount: keep the "Save N prices" button honest.
export default class extends Controller {
  static targets = ["listSelect", "newListWrap", "applyCount"]
  static values = {
    gradeMap: { type: Object, default: {} },
    variantMap: { type: Object, default: {} }
  }

  connect() {
    this.recount()
  }

  editMatch(event) {
    const row = event.target.closest("[data-plr-item]")
    if (!row) return

    const editor = row.querySelector("[data-plr-editor]")
    const summary = row.querySelector("[data-plr-summary]")
    if (editor) editor.hidden = false
    if (summary) summary.hidden = true

    const firstSelect = editor && editor.querySelector("select")
    if (firstSelect) firstSelect.focus()
  }

  familyChanged(event) {
    const row = event.target.closest("[data-plr-item]")
    if (!row) return

    this.applyFamily(row, event.target.value)
    this.maybeOfferCascade(row)
    this.recount()
  }

  applyCascade(event) {
    const sourceRow = event.target.closest("[data-plr-item]")
    if (!sourceRow) return

    const familyId = this.familySelect(sourceRow)?.value
    const base = this.baseName(this.rowDescription(sourceRow))
    if (!familyId || !base) return

    this.siblingRows(sourceRow, base).forEach((row) => {
      const select = this.familySelect(row)
      if (!select) return
      select.value = familyId
      this.applyFamily(row, familyId)

      // Reveal the now-resolved sibling so the operator sees it filled in.
      const editor = row.querySelector("[data-plr-editor]")
      const summary = row.querySelector("[data-plr-summary]")
      if (editor) editor.hidden = false
      if (summary) summary.hidden = true
    })

    this.hideCascade(sourceRow)
    this.recount()
  }

  gradeChanged(event) {
    this.syncNewGradeName(event.target.closest("[data-plr-item]"))
    this.recount()
  }

  variantChanged(event) {
    const row = event.target.closest("[data-plr-item]")
    if (!row) return

    const grade = row.querySelector("[data-plr-grade]")
    if (grade) {
      if (event.target.value) {
        // Overrides are group-less by design: park the group pick and disable
        // the select so it neither posts nor reads as part of the row's key.
        if (!grade.disabled) grade.dataset.plrParkedGrade = grade.value
        grade.value = ""
        grade.disabled = true
      } else {
        grade.disabled = false
        grade.value = grade.dataset.plrParkedGrade || ""
        delete grade.dataset.plrParkedGrade
      }
    }
    this.syncNewGradeName(row)
    this.recount()
  }

  variantToggled(event) {
    const row = event.target.closest("[data-plr-item]")
    if (!row) return

    const select = row.querySelector("[data-plr-variant]")
    if (select) select.hidden = false
    event.target.hidden = true
  }

  listChanged() {
    if (!this.hasNewListWrapTarget) return

    const pickedExisting = this.listSelectTarget.value !== ""
    this.newListWrapTarget.style.display = pickedExisting ? "none" : ""
  }

  skipToggled(event) {
    const row = event.target.closest("[data-plr-item]")
    if (!row) return

    row.classList.toggle("plr-skipped", event.target.checked)
    this.recount()
  }

  // --- internals -----------------------------------------------------------

  // The shared effect of choosing a product for a row, reused by both a
  // direct pick (familyChanged) and a cascade (applyCascade).
  applyFamily(row, familyId) {
    this.repopulateGrades(row, familyId)
    this.repopulateVariants(row, familyId)
    this.autofillGrade(row, familyId)

    // A hand-picked product is a correction/confirmation worth learning;
    // clearing the select back to blank withdraws it.
    const learn = row.querySelector("[data-plr-learn]")
    if (learn) learn.value = familyId ? "1" : "0"
  }

  // Pre-select the price group from the sheet's own color wording so a
  // product correction doesn't force a second manual pick. Mirrors the
  // server's resolution loosely: blank/"normal" wording → the default group;
  // otherwise the group whose name best overlaps the wording; no confident
  // overlap → left blank for the operator.
  autofillGrade(row, familyId) {
    const select = row.querySelector("[data-plr-grade]")
    const grades = this.gradeMapValue[familyId] || []
    if (!select || grades.length === 0) return

    const tokens = this.normalizeTokens(this.rowQualifier(row))

    let chosen = null
    if (tokens.length === 0 || (tokens.length === 1 && tokens[0] === "normal")) {
      chosen = grades.find((g) => g.default) || null
    } else {
      let best = 0
      grades.forEach((g) => {
        const gTokens = this.normalizeTokens(g.name)
        if (gTokens.length === 0) return
        const overlap = gTokens.filter((t) => tokens.includes(t)).length
        // Require containment one way or the other — a single shared word
        // isn't enough to auto-pick (the operator can still choose).
        const contained = gTokens.every((t) => tokens.includes(t)) || tokens.every((t) => gTokens.includes(t))
        if (overlap > best && contained) {
          best = overlap
          chosen = g
        }
      })
    }

    if (chosen) {
      select.value = chosen.id
      this.syncNewGradeName(row)
    }
  }

  // Show "use this product for the other N rows like it" when picking a
  // product here could settle sibling unmatched rows sharing its base name.
  maybeOfferCascade(row) {
    const cascade = row.querySelector("[data-plr-cascade]")
    if (!cascade) return

    const familyId = this.familySelect(row)?.value
    const base = this.baseName(this.rowDescription(row))
    const siblings = familyId && base ? this.siblingRows(row, base) : []

    if (siblings.length === 0) {
      this.hideCascade(row)
      return
    }

    const button = cascade.querySelector("[data-plr-cascade-btn]")
    if (button) {
      const noun = siblings.length === 1 ? "row" : "rows"
      button.textContent = `Use this product for ${siblings.length} more “${base}” ${noun}`
    }
    cascade.hidden = false
  }

  hideCascade(row) {
    const cascade = row.querySelector("[data-plr-cascade]")
    if (cascade) cascade.hidden = true
  }

  // Other rows still missing a product whose sheet description shares this
  // row's base name (same product, different width/size).
  siblingRows(sourceRow, base) {
    return Array.from(this.element.querySelectorAll("[data-plr-item]")).filter((row) => {
      if (row === sourceRow) return false
      const select = this.familySelect(row)
      if (!select || select.value) return false // already has a product
      return this.baseName(this.rowDescription(row)) === base
    })
  }

  // Keep the save button's count matched to what will actually be written:
  // a row imports when it isn't skipped, carries a price above zero, and is
  // pointed at a product (a family/group pick or a single-item override).
  recount() {
    if (!this.hasApplyCountTarget) return

    let count = 0
    this.element.querySelectorAll("[data-plr-item]").forEach((row) => {
      const skip = row.querySelector('input[type="checkbox"][value="skip"]')
      if (skip && skip.checked) return

      const priceInput = row.querySelector(".plr-price")
      if (!priceInput || !(parseFloat(priceInput.value) > 0)) return

      const family = this.familySelect(row)
      const variant = row.querySelector("[data-plr-variant]")
      if ((family && family.value) || (variant && variant.value)) count += 1
    })

    this.applyCountTarget.textContent = count
  }

  repopulateGrades(row, familyId) {
    const select = row.querySelector("[data-plr-grade]")
    if (!select) return

    const grades = this.gradeMapValue[familyId] || []
    const newGradeInput = row.querySelector("[data-plr-new-grade]")

    select.innerHTML = ""
    select.add(new Option("Pick a price group…", ""))
    grades.forEach((grade) => select.add(new Option(grade.name, grade.id)))
    if (newGradeInput?.dataset.suggested) {
      select.add(new Option(`Add a new group: "${newGradeInput.dataset.suggested}"`, NEW_GRADE_VALUE))
    }
    select.value = ""
    this.syncNewGradeName(row)
  }

  repopulateVariants(row, familyId) {
    const select = row.querySelector("[data-plr-variant]")
    if (!select) return

    const variants = this.variantMapValue[familyId] || []
    select.innerHTML = ""
    select.add(new Option("Use the price group above (most common)", ""))
    variants.forEach((v) => select.add(new Option(v.label, v.id)))
    select.value = ""
    if (variants.length === 0) select.hidden = true

    const toggle = row.querySelector("[data-plr-variant-toggle]")
    if (toggle) toggle.hidden = !select.hidden || variants.length === 0

    const grade = row.querySelector("[data-plr-grade]")
    if (grade) {
      grade.disabled = false
      delete grade.dataset.plrParkedGrade
    }
  }

  syncNewGradeName(row) {
    if (!row) return

    const select = row.querySelector("[data-plr-grade]")
    const newGradeInput = row.querySelector("[data-plr-new-grade]")
    if (!select || !newGradeInput) return

    newGradeInput.value =
      select.value === NEW_GRADE_VALUE ? (newGradeInput.dataset.suggested || "") : ""
  }

  // --- small helpers -------------------------------------------------------

  familySelect(row) {
    return row.querySelector('select[name$="[family_id]"]')
  }

  rowDescription(row) {
    return row.querySelector('input[name$="[description]"]')?.value || ""
  }

  rowQualifier(row) {
    return row.querySelector('input[name$="[qualifier]"]')?.value || ""
  }

  // The product name without size/width parentheticals — "Premium PU (40\")"
  // and "Premium PU (13\")" share the base "premium pu" so they cascade.
  baseName(description) {
    return description
      .toLowerCase()
      .split("(")[0]
      .replace(/[0-9].*$/, "")
      .replace(/\s+/g, " ")
      .trim()
  }

  normalizeTokens(text) {
    return String(text)
      .toLowerCase()
      .replace(/\(([^)]*)\)/g, " $1 ")
      .replace(/[^a-z0-9\s]/g, " ")
      .split(/\s+/)
      .filter(Boolean)
      .filter((t) => !/^\d+(\.\d+)?$/.test(t))
      .map((t) => STEMS[t] || t)
      .filter((t) => !STOPWORDS.has(t))
  }
}
